import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Badge } from './ui/badge';
import { Shield, AlertTriangle, Globe, Info, CheckCircle } from 'lucide-react';

// Risk level display configuration
const RISK_DISPLAY = {
  NONE: {
    emoji: '✅',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    label: 'Aucun risque',
    labelEn: 'Clear'
  },
  MODERATE: {
    emoji: '🟡',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    label: 'Risque faible',
    labelEn: 'Low Risk'
  },
  HIGH: {
    emoji: '🟠',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/30',
    label: 'Risque élevé',
    labelEn: 'Elevated Risk'
  },
  CRITICAL: {
    emoji: '🔴',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
    label: 'Risque critique',
    labelEn: 'Critical Risk'
  },
  UNKNOWN: {
    emoji: '⚪',
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
    borderColor: 'border-gray-500/30',
    label: 'Non vérifié',
    labelEn: 'Not Screened'
  }
};

// Country risk level colors
const COUNTRY_RISK_COLORS = {
  VERY_LOW: 'text-emerald-400',
  LOW: 'text-green-400',
  MODERATE: 'text-yellow-400',
  HIGH: 'text-orange-400',
  VERY_HIGH: 'text-red-400',
  UNKNOWN: 'text-gray-400',
  STABLE: 'text-emerald-400'
};

/**
 * Compact sanctions badge for screener table
 */
export const SanctionsBadgeCompact = ({ riskLevel }) => {
  const config = RISK_DISPLAY[riskLevel] || RISK_DISPLAY.UNKNOWN;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.color} ${config.borderColor} border cursor-help`}>
            <span>{config.emoji}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
          <div className="text-sm">
            <p className="font-medium">{config.label}</p>
            <p className="text-gray-400 text-xs mt-1">
              Screening OFAC/UN/UE
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Full sanctions risk card for stock detail page
 */
export const SanctionsRiskCard = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-400">
          <Shield className="w-5 h-5" />
          <span>Données de sanctions non disponibles</span>
        </div>
      </div>
    );
  }
  
  const {
    risk_level,
    matches_found,
    screening_results,
    country_risk,
    company_name,
    api_status,
    last_checked
  } = data;
  
  const config = RISK_DISPLAY[risk_level] || RISK_DISPLAY.UNKNOWN;
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Screening Sanctions & ESG</h3>
        </div>
        <Badge className={`${config.bgColor} ${config.color} ${config.borderColor} border`}>
          {config.emoji} {config.label}
        </Badge>
      </div>
      
      {/* Risk Level Banner — guard against undefined matches_found when API failed */}
      {api_status !== 'success' && matches_found == null ? (
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-yellow-400">Screening sanctions non disponible</p>
              <p className="text-gray-400 text-sm">
                Aucune réponse du fournisseur de données (OFAC/UN/UE) pour ce ticker. Le profil de risque pays ci-dessous reste affiché à titre indicatif.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className={`p-3 rounded-lg ${config.bgColor} ${config.borderColor} border`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{config.emoji}</span>
            <div>
              <p className={`font-medium ${config.color}`}>{config.label}</p>
              <p className="text-gray-400 text-sm">
                {matches_found == null
                  ? 'Données de screening indisponibles'
                  : matches_found === 0
                    ? 'Aucune correspondance avec les listes de sanctions'
                    : `${matches_found} correspondance(s) trouvée(s)`}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Matches Found (if any) */}
      {screening_results && screening_results.length > 0 && (
        <div className="border border-orange-500/30 rounded-lg p-3 bg-orange-500/10">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <h4 className="text-orange-400 font-medium">Correspondances détectées</h4>
          </div>
          <div className="space-y-2">
            {screening_results.map((match, idx) => (
              <div key={idx} className="bg-white/5 rounded p-2 text-sm">
                <p className="text-white font-medium">{match.name}</p>
                <p className="text-gray-400">Source: {match.source}</p>
                <p className="text-gray-500 text-xs">
                  Similarité: {match.similarity_score}%
                </p>
              </div>
            ))}
          </div>
          <p className="text-orange-400 text-xs mt-2">
            ⚠️ Due diligence approfondie recommandée
          </p>
        </div>
      )}
      
      {/* Country Risk Profile */}
      {country_risk && (
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-5 h-5 text-purple-400" />
            <h4 className="text-white font-medium">Profil de Risque Pays</h4>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/5 rounded p-2">
              <p className="text-gray-400 text-xs">Risque Sanctions</p>
              <p className={`font-medium ${COUNTRY_RISK_COLORS[country_risk.sanctions_risk] || 'text-gray-400'}`}>
                {country_risk.sanctions_risk || 'N/A'}
              </p>
            </div>
            
            <div className="bg-white/5 rounded p-2">
              <p className="text-gray-400 text-xs">Contrôle Capitaux</p>
              <p className={`font-medium ${COUNTRY_RISK_COLORS[country_risk.capital_controls] || 'text-gray-400'}`}>
                {country_risk.capital_controls || 'N/A'}
              </p>
            </div>
            
            <div className="bg-white/5 rounded p-2">
              <p className="text-gray-400 text-xs">Risque Devise</p>
              <p className={`font-medium ${COUNTRY_RISK_COLORS[country_risk.currency_risk] || 'text-gray-400'}`}>
                {country_risk.currency_risk || 'N/A'}
              </p>
            </div>
            
            <div className="bg-white/5 rounded p-2">
              <p className="text-gray-400 text-xs">Stabilité Politique</p>
              <p className={`font-medium ${COUNTRY_RISK_COLORS[country_risk.political_stability] || 'text-gray-400'}`}>
                {country_risk.political_stability || 'N/A'}
              </p>
            </div>
          </div>
          
          {country_risk.notes_fr && (
            <div className="mt-3 p-2 bg-white/5 rounded flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-gray-300 text-sm">{country_risk.notes_fr}</p>
            </div>
          )}
        </div>
      )}
      
      {/* API Status & Disclaimer */}
      <div className="border-t border-white/10 pt-3 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {api_status === 'success' ? (
            <CheckCircle className="w-4 h-4 text-emerald-500" />
          ) : (
            <Info className="w-4 h-4 text-yellow-500" />
          )}
          <span>
            {api_status === 'success' 
              ? 'Screening via OFAC, UN, UE' 
              : 'Profil de risque pays basé sur les données géopolitiques courantes. Indépendant du screening sanctions (cf. message ci-dessus si applicable).'
            }
          </span>
        </div>
        
        {last_checked && (
          <p className="text-gray-500 text-xs">
            Dernière vérification: {new Date(last_checked).toLocaleDateString('fr-FR')}
          </p>
        )}
        
        <p className="text-gray-600 text-xs italic">
          Disclaimer: Ce screening est indicatif. Consultez un conseiller juridique 
          pour toute décision d'investissement impliquant des considérations de sanctions.
        </p>
      </div>
    </div>
  );
};

export default SanctionsRiskCard;
